// Styles
import "./apps.scss";
// Components
import { AppsHeader } from "./components/apps-header/AppsHeader";
import { AppRow } from "./components/app-row/AppRow";
import { EmptyList } from "../../../../../../../../shared/components/empty-list/EmptyList";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { hideModuleThunk } from "../../../../utils/module-utils/hide-module";
import { CircleLoader } from "../../../../../../../../shared/components/circle-loader/CircleLoader";
import { CompactSelect } from "../../../../../../../../shared/components/selects/compact-select/CompactSelect";
import { useDashboardData } from "../../../../../../service/dashboard-data.service";
import { useState, useEffect } from "react";
import {
	buildSelectStatuses,
	AccountSelectItem,
} from "../../../../utils/component-utils/build-select-statuses";
import { statusMatchesFilter } from "../../../../../../../../shared/utils/normalize-status-label";
// Utils
import { processApps } from "./utils/processApps";
import { filterPendingTickets } from "./utils/filterPendingTickets";
import {
	appStatusForFilterSelect,
	prepareAppsForDisplay,
} from "./utils/mapAppStatus";
import {
	setSortParameters,
	getSortParameters,
} from "./utils/setSortParameters";
import {
	setFilterParameters,
	getFilterParameters,
} from "./utils/setFilterParameters";
import { sumCostColumnUsd } from "../../../../../../../../shared/utils/sum-cost-column-usd";
import { RootState } from "../../../../../../../../shared/store/store";
// Service
import { useGetAllAppTickets } from "./service/app-ticket.service";
// ------ Constants
const appsImage = "/img/icons/large-64/dots.png";

export const Apps = () => {
	// ------ Redux
	const dispatch = useDispatch<any>();

	// ------ Queries
	const { data, isAwaitingDashboardContent } = useDashboardData();
	const { data: appTicketsData, isLoading: isLoadingAppTickets } =
		useGetAllAppTickets();

	// ------ States
	const highlightedAppIds = useSelector(
		(state: RootState) => state.app.highlightedApps.map((h) => h.appId),
		shallowEqual,
	);
	const [accountSelectItems, setAccountSelectItems] = useState<
		AccountSelectItem[]
	>([]);
	const [selectedStatus, setSelectedStatus] = useState<string>(
		getFilterParameters() ?? "all",
	);
	// Критерий и способ сортировки
	const [sortInfo, setSortInfo] = useState({
		sortMethod: getSortParameters().sortMethod ?? "asc",
		sortBy: getSortParameters().sortBy ?? "status",
	});

	// ------ Functions
	const handleSort = (method: string, by: string) => {
		setSortInfo({ sortMethod: method, sortBy: by });
		setSortParameters(method, by);
	};

	// ------ Effects
	useEffect(() => {
		const appsDataInner = data?.apps_info?.data ?? [];
		const ticketsFiltered = filterPendingTickets(
			appTicketsData?.data?.tickets,
		);
		const appsForStatuses = appsDataInner.map((app) => ({
			status: appStatusForFilterSelect(app.status),
		}));
		const forStatuses = [...ticketsFiltered, ...appsForStatuses];
		buildSelectStatuses(forStatuses, "status", setAccountSelectItems);
	}, [data, appTicketsData]);

	useEffect(() => {
		if (selectedStatus === "all") return;
		if (isAwaitingDashboardContent) return;
		if (isLoadingAppTickets) return;
		if (accountSelectItems.length === 0) return;
		// Поки в списку лише «Всі статуси», дані ще не підвантажились — не скидаємо збережений фільтр
		if (
			accountSelectItems.length === 1 &&
			accountSelectItems[0]?.key === "all"
		) {
			return;
		}
		if (!accountSelectItems.some((i) => i.key === selectedStatus)) {
			setSelectedStatus("all");
			setFilterParameters("all");
		}
	}, [
		accountSelectItems,
		selectedStatus,
		isAwaitingDashboardContent,
		isLoadingAppTickets,
	]);

	const handleSelect = (key: string) => {
		setSelectedStatus(key);
		setFilterParameters(key);
	};

	const sortedApps = processApps(
		prepareAppsForDisplay(data?.apps_info?.data),
		selectedStatus,
		sortInfo,
	);

	const ticketsForList = filterPendingTickets(appTicketsData?.data?.tickets);
	const filteredTickets = ticketsForList.filter((item) =>
		statusMatchesFilter(item.status, selectedStatus),
	);
	const mergedRows = [
		...filteredTickets.map((ticket) => ({ row: "ticket" as const, ticket })),
		...sortedApps.map((app) => ({ row: "app" as const, app })),
	];

	const appsTotalCostUsd = sumCostColumnUsd(sortedApps);

	return (
		<div className='apps window' data-app-highlight-boundary=''>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/apps.png' alt='apps icon' />
					<div>Apps</div>
				</div>
				<div className='window__header_center'>
					<CompactSelect
						items={accountSelectItems}
						value={selectedStatus}
						onSelect={handleSelect}
					/>
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide apps'
						onClick={() => dispatch(hideModuleThunk("apps"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div className='window__body'>
				<AppsHeader
					setSortInfo={sortInfo}
					handleSort={handleSort}
					totalCostUsd={appsTotalCostUsd}
				/>
				{isAwaitingDashboardContent ? (
					<div className='window__loader'>
						<CircleLoader />
					</div>
				) : (
					<div className='window__body_list'>
						<div className='window__body_list-inner'>
							{mergedRows.map((entry, rowIndex) =>
								entry.row === "ticket" ? (
									<AppRow
										key={`ticket-${entry.ticket.id}-${rowIndex}`}
										id={entry.ticket.bundle_id}
										status={entry.ticket.status}
										cost={0}
										is_linked={false}
										all_accounts_available={false}
										accounts={[]}
										isTicketRow
										ticketId={entry.ticket.id}
									/>
								) : (
									<AppRow
										key={`app-${rowIndex}-${entry.app.app_id}`}
										id={entry.app.app_id}
										status={entry.app.status}
										cost={entry.app.cost_usd}
										is_linked={entry.app.is_linked}
										all_accounts_available={entry.app.all_accounts_available}
										accounts={entry.app.accounts}
										dimmed={
											highlightedAppIds.length > 0 &&
											!highlightedAppIds.includes(entry.app.app_id)
										}
									/>
								),
							)}
						</div>
					</div>
				)}
				{!isAwaitingDashboardContent &&
					!isLoadingAppTickets &&
					mergedRows.length === 0 && (
						<div className='window__loader'>
							<EmptyList image={appsImage} text={"Застосунки відсутні"} />
						</div>
					)}
			</div>
		</div>
	);
};
